import React, { useState, useEffect, useRef } from "react";
import { logger } from '../../../utils/logger'
import "./WebRtc.css";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import { useGamepads } from "react-gamepads";

interface WebRtcProps {
  drone: Drone | null;
  mode: string;
}

const WebRtc: React.FC<WebRtcProps> = ({ drone, mode }) => {
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [websocket, setWebSocket] = useState<WebSocket | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 300, height: 200 });
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [waitVideo, setWaitVideo] = useState(false);
  const [codecSupportedText, setCodecSupportedText] = useState<string | null>();
  const ice = useRef<IceConfig | null>(null);

  useEffect(() => {
    return () => {
      websocket?.close();
    };
  }, []);


  useEffect(() => {
    if (drone === null) {
      return;
    }
    stop();
    setShowPlayButton(true);
    setWaitVideo(false);
    setCodecSupportedText(null);
  }, [drone]);

  useEffect(() => {
    if (mode != "fpv") {
      return;
    }
    stop();
    setShowPlayButton(true);
    setWaitVideo(false);
    setCodecSupportedText(null);
  }, [mode]);

  const handlePlayClick = () => {
    if (checkAvailable(drone)) {
        axios
            .get<IceConfig>("api/fpv/ice?imei=" + drone?.imei)
            .then((response) => {
                const iceConfig = response.data;
                ice.current = iceConfig;
                logger.debug(response.data);
                createWs(iceConfig);
                setShowPlayButton(false);
                setWaitVideo(true);
            })
            .catch((error) => {
                logger.error(error.response);
            });
    }
  };

  function createPeerConnection(): RTCPeerConnection {
    if (!ice.current) {
      throw new Error("ICE config not loaded");
    }

    const pc = new RTCPeerConnection({
      iceServers: ice.current.iceServers,
    });

    pcRef.current = pc;

    pc.addEventListener("iceconnectionstatechange", () =>
      logger.debug(" -> " + pc.iceConnectionState)
    );

    pc.addEventListener("icegatheringstatechange", () =>
      logger.debug(" -> " + pc.iceGatheringState)
    );

    pc.addEventListener("signalingstatechange", () =>
      logger.debug(" -> " + pc.signalingState)
    );

    /*pc.ondatachannel = (event) => {
      const dataChannel = event.channel;
      logger.debug("📥 Входящий DataChannel:", dataChannel.label);

      dataChannel.onopen = () => {
        logger.debug("✅ DataChannel открыт");
      };

      dataChannel.onmessage = (e) => {
        logger.debug("📨 Сообщение получено:", e.data);
      };

      dataChannel.onclose = () => {
        logger.debug("❌ DataChannel закрыт");
      };

      dataChannel.onerror = (e) => {
        console.error("⚠️ Ошибка в DataChannel:", e);
      };
    };*/

    pc.ontrack = (evt) => {
      const videoEl = videoRef.current;
      if (!videoEl) return;

      const remoteDesc = pc.remoteDescription?.sdp;

      if (remoteDesc) {
        if (remoteDesc.includes('H265') || remoteDesc.includes('HEVC')) {
          if (!isH265Supported()) {
            setCodecSupportedText("Кодек H.265 не поддерживается браузером");
          }
        }
      }

      videoEl.srcObject = evt.streams[0];
      videoEl.onloadedmetadata = () => {
        videoEl
          .play()
          .then(() => {
            setShowPlayButton(false);
            setWaitVideo(false);
          })
          .catch((e) => console.warn("play() error:", e));
      };
    };

    return pc;
  }

  function createWs(iceConfig: IceConfig) {
    if (!iceConfig || !drone) return;
    const wsstr = `${iceConfig.ws.replace("wstest", "connect")}?token=${iceConfig.wsToken}`;
    logger.debug(wsstr);
    const ws = new WebSocket(wsstr);
    setWebSocket(ws);

    ws.onopen = () => {
      sendRequest(ws);
      logger.debug("ws open");
    };

    ws.onmessage = async (evt) => {
      if (typeof evt.data !== "string") {
        return;
      }
      if (evt.data === "partner connect") {
        logger.debug("partner connect");
      } else {
        const message = JSON.parse(evt.data);
        if (message.type === "offer") {
          logger.debug("offer-sdp: " + message.sdp);
          await handleOffer(message, ws);
        }
      }
    };
  }

  function sendRequest(ws: WebSocket) {
    ws.send(
      JSON.stringify({
        id: "server",
        type: "request",
      })
    );
  }

  async function handleOffer(offer: RTCSessionDescriptionInit, ws: WebSocket) {
    const p = createPeerConnection();
    await p.setRemoteDescription(offer);
    pcRef.current = p;
    await sendAnswer(p, ws);
  }

  async function sendAnswer(p: RTCPeerConnection, ws: WebSocket) {
    await p.setLocalDescription(await p.createAnswer());
    await waitGatheringComplete(p);

    const answer = p.localDescription;
    if (!answer) return;
    logger.debug("answer-sdp: " + answer.sdp);

    ws.send(
      JSON.stringify({
        id: "server",
        type: answer.type,
        sdp: answer.sdp,
      })
    );
  }

  async function waitGatheringComplete(p: RTCPeerConnection) {
    return new Promise<void>((resolve) => {
      if (p.iceGatheringState === "complete") {
        resolve();
      } else {
        p.addEventListener("icegatheringstatechange", () => {
          if (p.iceGatheringState === "complete") {
            resolve();
          }
        });
      }
    });
  }

  function stop() {
    if (!pcRef?.current) return;

    if (pcRef.current.getTransceivers) {
      pcRef.current.getTransceivers().forEach((transceiver) => {
        if (transceiver.stop) {
          transceiver.stop();
        }
      });
    }

    pcRef.current.getSenders().forEach((sender) => {
      sender.track?.stop();
    });

    pcRef.current.close();
    pcRef.current = null;
  }

  const startResize = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
    window.addEventListener("mousemove", onResize);
    window.addEventListener("mouseup", stopResize);
  };

  const onResize = (e: MouseEvent) => {
    if (!videoContainerRef.current) return;
    const rect = videoContainerRef.current.getBoundingClientRect();
    const newWidth = rect.right - e.clientX;
    const newHeight = rect.bottom - e.clientY;
    setDimensions({
      width: Math.min(window.innerWidth - 100, Math.max(200, newWidth)),
      height: Math.min(window.innerHeight - 100, Math.max(150, newHeight)),
    });
  };

  const stopResize = () => {
    setIsResizing(false);
    window.removeEventListener("mousemove", onResize);
    window.removeEventListener("mouseup", stopResize);
  };

  const checkAvailable = (d: Drone | null): boolean => {
    if (!d) return false;
    if (d.version < 2002) return false;
    return true;
  };

  function isH265Supported(): boolean {
    const codecs = RTCRtpReceiver.getCapabilities('video')?.codecs ?? [];
    return codecs.some(c => /h265|hevc/i.test(c.mimeType));
  }

  return (
    <>
    { mode == "normal" && 
     <>
      {!checkAvailable(drone) ? null : (
        <div
          id="media"
          ref={videoContainerRef}
          className="video-container"
          style={{
            display: "block",
            width: dimensions.width,
            height: dimensions.height,
          }}
        >
          <div className="video-resize-handle" onMouseDown={startResize}></div>
          <video
            ref={videoRef}
            id="video"
            autoPlay
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
          {showPlayButton && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              <button
                onClick={handlePlayClick}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
              >
                ▶ Play (Это окно для проверки камеры, для полетов используйте FPV режим)
              </button>
            </div>
          )}

          {waitVideo && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}

          { codecSupportedText && <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <div>{codecSupportedText}</div>
          </div>}
        </div>
      )}
    </>}
    </>
  );
};

export default WebRtc;
